import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Mail, Send } from "lucide-react";
import { Reveal, SectionHeading } from "./Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ContactSection = () => {
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [sending, setSending] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/enquiries`, { ...form, interest: "contact" });
      toast.success("Message sent! We'll get back to you soon.");
      setForm({ name: "", email: "", message: "" });
    } catch {
      toast.error("Could not send your message. Please email us at info@iqmize.com.");
    } finally {
      setSending(false);
    }
  };

  const inputCls =
    "w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm font-semibold text-slate-800 placeholder:font-normal placeholder:text-slate-400 focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/20 transition-[border-color,box-shadow] duration-200";

  return (
    <section id="contact" data-testid="contact-section" className="py-20 lg:py-24 bg-white">
      <div className="max-w-3xl mx-auto px-5 lg:px-8">
        <SectionHeading
          align="center"
          eyebrow="Contact IQMIZE"
          title="Questions? Write to us."
          description="Ask about the book, mentoring programs, or anything else — every message lands directly in our inbox."
        />

        <Reveal delay={0.1}>
          <form
            onSubmit={submit}
            data-testid="contact-form"
            className="mt-10 rounded-3xl border border-slate-200 bg-brand-light/50 p-7 lg:p-10 shadow-[0_8px_30px_rgb(0,0,0,0.05)]"
          >
            <div className="grid sm:grid-cols-2 gap-4">
              <input
                required
                data-testid="contact-name-input"
                className={inputCls}
                placeholder="Your name"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
              />
              <input
                required
                type="email"
                data-testid="contact-email-input"
                className={inputCls}
                placeholder="Your email address"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
              />
            </div>
            <textarea
              required
              data-testid="contact-message-input"
              className={`${inputCls} mt-4 min-h-[120px] resize-y`}
              placeholder="Your message"
              value={form.message}
              onChange={(e) => setForm({ ...form, message: e.target.value })}
            />
            <button
              type="submit"
              disabled={sending}
              data-testid="contact-form-submit-button"
              className="mt-5 flex w-full items-center justify-center gap-2.5 rounded-full bg-brand px-8 py-4 text-base font-bold text-white shadow-md transition-[transform,box-shadow,background-color,opacity] duration-300 hover:-translate-y-1 hover:bg-brand-dark hover:shadow-xl disabled:opacity-60 disabled:hover:translate-y-0"
            >
              <Send size={18} /> {sending ? "Sending..." : "Send Message"}
            </button>
            <p className="mt-4 flex items-center justify-center gap-2 text-xs font-semibold text-slate-500">
              <Mail size={13} className="text-brand" /> Every message is emailed to info@iqmize.com
            </p>
          </form>
        </Reveal>
      </div>
    </section>
  );
};

export default ContactSection;
